// Based on https://github.com/epoberezkin/fast-json-stable-stringify

/*
This software is released under the MIT license:

Copyright (c) 2021 Higharc Inc.
Copyright (c) 2017 Evgeny Poberezkin
Copyright (c) 2013 James Halliday

Permission is hereby granted, free of charge, to any person obtaining a copy of
this software and associated documentation files (the "Software"), to deal in
the Software without restriction, including without limitation the rights to
use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
the Software, and to permit persons to whom the Software is furnished to do so,
subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

export interface StringifyOptions {
  cmp?: (aKey: any, bKey: any) => number;
  throwOnCycle?: boolean;
  indent?: string;
  replacer?: (key: string, value: any) => any;
  reviver?: (key: string, value: any) => any;
}

/**
 * A replacement for JSON.stringify that sorts keys.
 * @param data Data to stringify.
 * @param opts May include a comparison function to compare keys. By default English alphabetical sorting is used.
 */
export function stableStringify(data: any, opts: StringifyOptions = {}): string {
  const defaultOpts: StringifyOptions = {
    cmp: function (a, b) {
      return a.localeCompare(b, "en");
    },
    throwOnCycle: true,
    indent: "  ",
  };
  opts = { ...defaultOpts, ...opts };

  const throwOnCycle = opts.throwOnCycle;
  const cmp = opts.cmp;
  const replacer = opts.replacer; // Replacer added

  function newLine(indentLevel: number) {
    let indent = "\n";
    for (let i = 0; i < indentLevel; ++i) {
      indent += opts.indent;
    }
    return indent;
  }

  const seen: any[] = [];

  function stringifyNode(node: any, indentLevel: number, key?: string): string | undefined {
    // Apply the replacer if it exists
    if (replacer) {
      node = replacer(key || "", node);
    }

    if (node && node.toJSON && typeof node.toJSON === "function") {
      node = node.toJSON();
    }

    if (node === undefined) {
      return undefined;
    }
    if (typeof node === "number") {
      return isFinite(node) ? "" + node : "null";
    }
    if (typeof node !== "object") {
      return JSON.stringify(node);
    }

    let out: string;
    if (Array.isArray(node)) {
      out = "[";
      for (let i = 0; i < node.length; i++) {
        if (i > 0) {
          out += ",";
        }
        // Undefined values inside arrays are converted to null. This matches the behavior of JSON.stringify.
        out += newLine(indentLevel + 1) + (stringifyNode(node[i], indentLevel + 1, String(i)) || "null");
      }
      return out + newLine(indentLevel) + "]";
    }

    if (node === null) {
      return "null";
    }

    if (seen.indexOf(node) !== -1) {
      if (!throwOnCycle) {
        return JSON.stringify("__cycle__");
      }
      throw new TypeError("Converting cyclic structure to JSON");
    }

    const seenIndex = seen.push(node) - 1;
    const keys = Object.keys(node).sort(cmp);
    out = "";
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = stringifyNode(node[key], indentLevel + 1, key);

      if (value === undefined) {
        continue;
      }
      if (out) {
        out += ",";
      }
      out += newLine(indentLevel + 1) + JSON.stringify(key) + ": " + value;
    }
    seen.splice(seenIndex, 1);
    return "{" + out + newLine(indentLevel) + "}";
  }

  const stringifiedData = stringifyNode(data, 0);
  if (stringifiedData === undefined) {
    return "undefined";
  }
  return stringifiedData;
}
